// CHG-1461 / ITER-3577 — v2 LoginPage.
// CHG-1510 / ITER-3628 (MFA program η.1) — routes MFA branches via the
// new AuthContext LoginResult discriminated union instead of catching
// MfaNotSupportedError. `mfa_required` → navigate /mfa-challenge
// carrying mfa_token + mfa_methods + returnTo. `mfa_setup_required`
// surfaces a "use v1 for now" message UNTIL η.2 (sibling-session)
// swaps that branch arm to navigate('/mfa-setup', ...). The two arms
// of the switch below are the merge surface — mechanical per
// /tmp/mfa-program.md §Coordination.
//
// Hand-rolled exception per feedback_deterministic_codegen_over_hand_rolling.md:
// the login state machine ends in "navigate to returnTo" rather than rendering
// a success UI, which doesn't fit the auth-flow generator's state-renders-UI
// pattern. The 5 other auth pages (reset-password / verify-email /
// accept-invite / sso-callback / register-from-email) terminate in a rendered
// confirmation; login does not.
//
// Backend: POST /login (LoginRequest in src/api/app/schemas/user.py).
//
// CHG-1510 (η.1) + CHG-1512 (η.2) — replaced the MfaNotSupportedError
// sentinel with a LoginResult discriminator. login() now returns
// { kind: "ok" | "mfa_required" | "mfa_setup_required" }; switch below
// routes each branch to its destination page (/mfa-challenge for η.1's
// MfaChallengePage, /mfa-setup for η.2's MfaSetupPage).

import { useState, type FormEvent } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { AuthLayout } from "./AuthLayout";
import { inputStyle, labelStyle, errorStyle, submitStyle } from "./styles";

type State = "form" | "submitting" | "error";

// Confine post-login navigation to in-app paths. Prevents open-redirect
// via ?returnTo=https://evil.example/.
function safeReturnTo(raw: string | null): string {
  if (!raw) return "/";
  if (!raw.startsWith("/") || raw.startsWith("//")) return "/";
  return raw;
}

export default function LoginPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [state, setState] = useState<State>("form");
  const [error, setError] = useState<string>("");

  const returnTo = safeReturnTo(params.get("returnTo"));

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    if (!email || !password) {
      setError("Email and password are required.");
      setState("error");
      return;
    }
    setState("submitting");
    try {
      const result = await login(email, password);
      switch (result.kind) {
        case "ok":
          navigate(returnTo, {
            replace: true,
            state: { from: location.pathname },
          });
          return;
        case "mfa_required":
          // CHG-1510 (η.1) — hand off to /mfa-challenge with the
          // mfa_token + mfa_methods. The page reads them from
          // location.state; returnTo travels along so the post-
          // verify navigate lands at the original target.
          navigate("/mfa-challenge", {
            replace: true,
            state: {
              mfa_token: result.mfa_token,
              mfa_methods: result.mfa_methods,
              returnTo,
            },
          });
          return;
        case "mfa_setup_required":
          // CHG-1512 (η.2) — replaced η.1's temporary "use v1 for now"
          // arm with a real /mfa-setup navigation. MfaSetupPage POSTs
          // /auth/mfa-setup/enroll on mount, shows the QR + recovery
          // codes, and on verify calls AuthContext.completeMfaLogin
          // before navigating returnTo.
          navigate("/mfa-setup", {
            replace: true,
            state: {
              setup_token: result.setup_token,
              reason_code: result.reason_code,
              returnTo,
            },
          });
          return;
      }
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(
        detail ?? "Sign-in failed. Check your email and password and try again.",
      );
      setState("error");
    }
  }

  const submitting = state === "submitting";

  return (
    <AuthLayout
      pageId="login"
      title="Sign in"
      subtitle="Welcome back. Enter your credentials to continue."
    >
      <form onSubmit={onSubmit} data-form="login">
        <label style={labelStyle}>
          Email
          <input
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={submitting}
            data-field="email"
            style={inputStyle}
          />
        </label>
        <label style={{ ...labelStyle, marginTop: 12 }}>
          Password
          <input
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={submitting}
            data-field="password"
            style={inputStyle}
          />
        </label>
        {error && (
          <div data-role="error" style={errorStyle}>
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={submitting}
          data-action="submit"
          style={submitStyle(submitting)}
        >
          {submitting ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </AuthLayout>
  );
}
